// @ts-ignore
import React, {
    createContext,
    PropsWithChildren,
    useContext,
    useEffect,
    useState
} from "react";

import { useIntl } from "react-intl";

import FeatureGates from "@atlassian/feature-gate-js-client";

import {
    useFetchUserSegmentData,
    useSearchParameters,
    useUserSegmentationStatus
} from "./";

export type JiraTemplateChoice = "kanban" | "scrum" | "business" | undefined;

type JiraTemplateChoiceProps = {
    jiraTemplateChoiceView: JiraTemplateChoiceView;
    shouldShowJiraTemplateChoiceScreen: boolean;
    loadingUserSegmentData: boolean;
    templateChoice?: JiraTemplateChoice;
    setTemplateChoice: (templateChoice?: JiraTemplateChoice) => void;
    templateChoiceError: Error | undefined;
    setTemplateChoiceError: (e: Error) => void;
    isUserSegmentationViewRequired: boolean;
};

export const JiraTemplateChoiceContext = createContext<JiraTemplateChoiceProps>(
    {
        jiraTemplateChoiceView: "control",
        shouldShowJiraTemplateChoiceScreen: false,
        loadingUserSegmentData: false,
        templateChoice: undefined,
        setTemplateChoice: () => undefined,
        templateChoiceError: undefined,
        setTemplateChoiceError: () => undefined,
        isUserSegmentationViewRequired: true
    }
);

export const JiraTemplateChoiceProvider = ({
    children
}: PropsWithChildren<unknown>) => {
    const isJiraTemplateChoiceFeatureGateEnabled = FeatureGates.checkGate(
        "jsw_transparent_template_over_confirmation_page"
    );

    const experimentConfig = FeatureGates.getExperiment(
        "jsw_signup_transparent_template_user_seg_redesign",
        { fireExperimentExposure: false }
    );

    const jiraTemplateChoiceView: JiraTemplateChoiceView =
        isJiraTemplateChoiceFeatureGateEnabled
            ? experimentConfig.get("jiraTemplateChoiceView", "control")
            : "control";

    const isUserSegmentationViewRequired: boolean =
        isJiraTemplateChoiceFeatureGateEnabled
            ? experimentConfig.get("isUserSegmentationViewRequired", true)
            : true;

    const { products } = useSearchParameters();
    const { canShowJiraTemplateChoiceScreen, loadingUserSegmentData } =
        useCanShowJiraTemplateChoiceScreen(
            products,
            isUserSegmentationViewRequired
        );

    const [templateChoice, setTemplateChoice] = useState<
        JiraTemplateChoice | undefined
    >();

    const [templateChoiceError, setTemplateChoiceError] = useState<
        Error | undefined
    >();

    const userHasPickedTemplate = templateChoice !== undefined;

    const shouldShowJiraTemplateChoiceScreen =
        templateChoiceError === undefined &&
        canShowJiraTemplateChoiceScreen &&
        !userHasPickedTemplate &&
        isJiraTemplateChoiceFeatureGateEnabled;

    // manually log exposure once the user satisfies all precheck conditions
    useEffect(() => {
        if (shouldShowJiraTemplateChoiceScreen) {
            fireExposureEvent(
                "jsw_signup_transparent_template_user_seg_redesign"
            );
        }
    }, [shouldShowJiraTemplateChoiceScreen]);

    return (
        <JiraTemplateChoiceContext.Provider
            value={{
                jiraTemplateChoiceView,
                templateChoice,
                loadingUserSegmentData,
                setTemplateChoice,
                shouldShowJiraTemplateChoiceScreen,
                templateChoiceError,
                setTemplateChoiceError,
                isUserSegmentationViewRequired
            }}
        >
            {children}
        </JiraTemplateChoiceContext.Provider>
    );
};

/* The user must pass the following conditions to see the template choice screen
   - locale: The user locale should be 'en' since this experiment will only run for the EN locale due to a
     short experiment lead time, factoring out translations to other locales
   - !hasTemplateRecommendation: Supplied via a query param, there must be no `templateId`
   - isProvisioningOnlyJSW: Provisioning only JSW and no other products
   - isProvisioningUserSegProducts: Is provisioning user segmentation products (which should be true if only JSW)
   - !isUserSegHiddenByQueryParam: User does not have `hideUserSegmentation=true` as a query param
   - !alreadyViewedUserSegmentationScreen: User is seeing segmentation screen for the first time. The
     user segmentation screen makes an API call to determine if the user has already seen the screen
     in the last 365 days. If they have seen it, the user seg screen is not displayed, and we do not meet
     our requirements to show the TTC either.
   - !isBusinessSegmentUser: The user must not be counted as a business user by user segmentation
   - !fetchError: This hook must access /gateway/api/me to check user segmentation and this call must succeed
     in order for the check above to be valid
 */
export const useCanShowJiraTemplateChoiceScreen = (
    products: readonly string[],
    isUserSegmentationViewRequired: boolean
) => {
    const {
        isProvisioningUserSegProducts,
        isUserSegHiddenByQueryParam,
        alreadyViewedUserSegmentationScreen,
        userSegmentationPageIsFinished
    } = useUserSegmentationStatus(products);

    const { locale } = useIntl();
    const hasTemplateRecommendation = Boolean(useSearchParameters().templateId);
    // don't make call to bxpkit-core for product key
    // caused build error
    const isProvisioningOnlyJSW =
        products.length === 1 && products[0] === "jira-software.ondemand";
    const exposedToUserSegScreen =
        isProvisioningUserSegProducts && !isUserSegHiddenByQueryParam;

    let canShowTTCPreFetchConditions =
        locale === "en" && !hasTemplateRecommendation && isProvisioningOnlyJSW; // TODO: Get rid of locale equals "en" condition for SPICE-1175 Productionize Jira Choice Template

    // verify the user has seen user segmentation before showing jira choice template.
    if (isUserSegmentationViewRequired) {
        canShowTTCPreFetchConditions =
            canShowTTCPreFetchConditions &&
            exposedToUserSegScreen &&
            !alreadyViewedUserSegmentationScreen;
    }

    const { fetched, fetchError, isBusinessSegmentUser } =
        useFetchUserSegmentData(
            canShowTTCPreFetchConditions && userSegmentationPageIsFinished
        );

    const canShowJiraTemplateChoiceScreen = isUserSegmentationViewRequired
        ? canShowTTCPreFetchConditions && !isBusinessSegmentUser && !fetchError
        : canShowTTCPreFetchConditions;

    return {
        canShowJiraTemplateChoiceScreen,
        loadingUserSegmentData: !fetched && isUserSegmentationViewRequired
    };
};

export const useJiraTemplateChoice = () => {
    return useContext(JiraTemplateChoiceContext);
};

export type JiraTemplateChoiceView = "control" | "transparent_template_v2";

function fireExposureEvent(experiment: string) {
    FeatureGates.manuallyLogExperimentExposure(experiment);
}
